import { useTour } from '@reactour/tour';
import { useQuery } from '@tanstack/react-query';
import { nanoid } from 'nanoid';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useTheme } from 'styled-components';

import { EditIcon } from '@/components/common/edit-icon';
import { useModal } from '@/components/common/modal';
import { PageLoadingSpinner } from '@/components/common/page-loading-spinner';
import { TopBar } from '@/components/common/top-bar';
import { UnlinkIcon } from '@/components/common/unlink-icon';
import { Warning } from '@/components/common/warning';
import { Button, ButtonVariant } from '@/components/form/button';
import { getTourStep } from '@/features/demo/steps';
import { BuddingMethods, useSettings } from '@/features/user-settings';
import { useExport } from '@/hooks/use-export/use-export';
import { useRoute } from '@/hooks/use-route';
import { MeasurementOverviewLayout } from '@/layouts';
import { isResultReady } from '@/utils/is-result-ready';

import { AnalysisInfo } from '../components/analysis-info';
import { EditAnalysisModal } from '../components/edit-analysis-modal';
import { HistogramChart } from '../components/histogram';
import { LinkFermentationModal } from '../components/link-modal';
import {
  BuddingIndexPercentage,
  ConcentrationPercentage,
  ViabilityPercentage,
  ViableConcentrationPercentage,
} from '../components/percentage-cards';
import { PictureCardContainer } from '../components/picture-card';
import { UnlinkFermentationModal } from '../components/unlink-modal';
import { getBatch } from '../services/batches';
import { SingleMeasurmentQueryKeys } from '../services/query-keys';
import {
  BioEthMethods,
  IBatch,
  Method,
  State,
  WarningLevel,
  YeastMethods,
} from '../types';
import { histogramParser } from '../utils/histogram-parser';

const maxStandardDeviation = import.meta.env.VITE_MAX_STANDARD_DEVIATION || 50;

interface IProps {
  batchApiHandler?: (id: string) => Promise<IBatch>;

  backButton?: () => void;

  onFermentationChange?: () => void;
}

// TODO: Error Boundary
export const AnalysisPage = ({
  batchApiHandler = getBatch,
  backButton,
  onFermentationChange,
}: IProps) => {
  const theme: any = useTheme();
  const { t } = useTranslation();
  const { settings } = useSettings();
  const { routeToAnalysisHistory, routeToPitchCalculator, routeToFermentationBatch } =
    useRoute();
  const { batchId = '' } = useParams();
  const [cacheKey, setCacheKey] = useState<string>(nanoid());

  // export analyses hook
  const { isExporting, isReportGenerating, generatePDFReport, generateExportZipBundle } =
    useExport();

  const { toggle, isOpen } = useModal();
  const linkFermentationToggle = useModal();
  const unlinkFermentationToggle = useModal();
  const [histogram, setHistogram] = useState<number[][]>([]);
  const [refetchInterval, setRefetchInterval] = useState<number | false>(2000); // 2 seoncds

  // demo
  const {
    isOpen: isTourOpen,
    setIsOpen: setIsTourOpen,
    setCurrentStep,
    currentStep,
    steps,
  } = useTour();
  const [wasTourOpen, setWasTourOpen] = useState<Boolean>(true);

  const { isLoading, isFetched, data } = useQuery({
    queryKey: [SingleMeasurmentQueryKeys.GetABatch, cacheKey, batchId],
    queryFn: () => batchApiHandler(batchId),
    refetchInterval,
  });

  const dead = useMemo(
    () => data?.resultStat.meanDiaDead?.hist[0] ?? [],
    [data?.resultStat],
  );
  const alive = useMemo(
    () => data?.resultStat.meanDiaAlive?.hist[0] ?? [],
    [data?.resultStat],
  );
  const meanDia = useMemo(
    () => data?.resultStat.meanDia?.hist[0] ?? [],
    [data?.resultStat],
  );

  // based on budding index method we calculate the budding
  const buddingIndex = useMemo(() => {
    const bud = data?.resultStat.bud || 0;
    // if not a bioeth method we just return the budding index as it is
    if (data?.method && !BioEthMethods.includes(data?.method)) return bud;
    // if bioeth we check whether it's budding only or all cells to change the % of budding index
    return settings.ethanolBuddingMethod === BuddingMethods.ALL_CELLS ? bud : bud / 2;
  }, [data?.resultStat.bud]);

  // fermentation id can be null if the analysis is not within a fermentation
  const fermentationId = useMemo(() => data?.parent ?? '', [data?.parent]);

  // a trick to wait until the data is loaded
  useEffect(() => {
    setWasTourOpen(isTourOpen); // remember the tour state
    setIsTourOpen(false); // temp turn off the tour, once data is loaded then we can set it open
  }, []);

  useEffect(() => {
    if (!data?.state) return;
    if (isResultReady(data?.state)) {
      // disable the polling, once we the analysis is completed
      setRefetchInterval(false);

      // update histogram data if state is ready
      const hist = histogramParser({ dead, alive, method: data!.method, meanDia });
      setHistogram(hist);

      // if tour was open, then we open the tour again
      if (wasTourOpen && currentStep === getTourStep(steps, 'eBHmWhV')) {
        setIsTourOpen(true);
        setCurrentStep(getTourStep(steps, '51oEgCY'));
      }
    }
  }, [data]);

  // handle loading state when measurement has been changed
  const onAnalysisChange = () => {
    setCacheKey(nanoid()); // change cache key to refetch data
    setRefetchInterval(2000); // restart polling
  };

  const isViabMethod = useMemo(() => {
    if (!data?.method) return false;
    return [Method.WineConzViab, Method.Yeast2ConzViab, Method.BioEthConzViab].includes(
      data?.method,
    );
  }, [data?.method]);

  const handleOnBack = () => {
    if (backButton) {
      backButton();
      return;
    }
    routeToAnalysisHistory();
  };

  const generateReport = () => {
    data &&
      generatePDFReport([
        { ...data, resultStat: { ...data.resultStat, bud: buddingIndex } },
      ]);
  };

  const exportReport = () => {
    data &&
      generateExportZipBundle([
        { ...data, resultStat: { ...data.resultStat, bud: buddingIndex } },
      ]);
  };

  // capture standard deviation warnings in the batch
  const hasStandardDeviationWarning = useMemo(() => {
    // get countTotal of each task into array
    const totalCellCountArray = data?.tasks.map((task) => task.resultStat.countTotal);
    if (totalCellCountArray === undefined || totalCellCountArray?.length < 1) {
      return false;
    }

    // get average value of the array
    const average =
      totalCellCountArray.reduce((a, b) => a + b, 0) / totalCellCountArray.length;
    if (average === 0) return false;

    // calculate the variance
    let varianceSum = 0;
    for (const totalCellCount of totalCellCountArray) {
      varianceSum += Math.pow(totalCellCount - average, 2);
    }
    const variance = varianceSum / totalCellCountArray.length;

    const standardDeviation = (Math.sqrt(variance) / average) * 100;
    return standardDeviation > maxStandardDeviation;
  }, [data?.resultStat]);

  // capture in concentration range warning
  const withinConcentrationRange = useMemo(() => {
    // get countTotal of each task into array
    const totalCellCountArray = data?.tasks.map((task) => task.resultStat.countTotal);
    if (
      data?.method === undefined ||
      totalCellCountArray === undefined ||
      totalCellCountArray?.length < 1
    ) {
      return true;
    }

    // get average value of the array
    const average =
      totalCellCountArray.reduce((a, b) => a + b, 0) / totalCellCountArray.length;
    const maxThreshold = YeastMethods.includes(data?.method) ? 100 : 150;

    if (average < 5 || average > maxThreshold) {
      return false;
    }
    return true;
  }, [data?.resultStat]);

  const onLinkToFermentation = (fermentationId: string) => {
    routeToFermentationBatch({ fermentationId, batchId });
  };

  const renderActionButtons = () => {
    return (
      <div className="flex gap-7">
        {isFetched ? (
          <>
            {fermentationId ? (
              <Button
                variant={ButtonVariant.Link}
                classnames="flex items-center gap-2"
                onClick={unlinkFermentationToggle.toggle}
                disabled={!isFetched}
              >
                <UnlinkIcon />
                <span className={`text-sm font-semibold text-${theme.current}-primary`}>
                  {t('fermentations.unlink.unlinkButton')}
                </span>
              </Button>
            ) : (
              <Button
                variant={ButtonVariant.Link}
                classnames="flex items-center gap-2"
                onClick={linkFermentationToggle.toggle}
                disabled={!isFetched}
              >
                <UnlinkIcon />
                <span className={`text-sm font-semibold text-${theme.current}-primary`}>
                  {t('fermentations.link.linkButton')}
                </span>
              </Button>
            )}
          </>
        ) : null}

        <Button
          id="step-edit-analysis"
          variant={ButtonVariant.Link}
          classnames="flex items-center gap-2"
          onClick={toggle}
          disabled={!isFetched}
        >
          <EditIcon />
          <span className={`text-sm font-semibold text-${theme.current}-primary`}>
            {t('common.edit')}
          </span>
        </Button>
      </div>
    );
  };

  return (
    <MeasurementOverviewLayout>
      <TopBar onBack={handleOnBack}>
        {!fermentationId ? renderActionButtons() : null}
      </TopBar>

      {isFetched && data && isResultReady(data?.state) ? (
        <>
          <EditAnalysisModal
            data={data}
            toggle={toggle}
            isOpen={isOpen}
            onSuccess={onAnalysisChange}
          />
          <UnlinkFermentationModal
            isOpen={unlinkFermentationToggle.isOpen}
            toggle={unlinkFermentationToggle.toggle}
            name={data.name}
            batchId={batchId}
            onSuccess={onFermentationChange}
          />
          <LinkFermentationModal
            isOpen={linkFermentationToggle.isOpen}
            toggle={linkFermentationToggle.toggle}
            batchId={batchId}
            onSuccess={onLinkToFermentation}
          />

          <MeasurementOverviewLayout.Title
            title={data?.name ? data?.name : data?.id.substring(0, 10)}
          >
            {fermentationId ? renderActionButtons() : null}
          </MeasurementOverviewLayout.Title>
        </>
      ) : null}

      <MeasurementOverviewLayout.Body>
        {isLoading ? <PageLoadingSpinner /> : null}

        {isFetched && [State.UploadCompleted, State.InProgress].includes(data!.state) ? (
          <PageLoadingSpinner>Analysis is still in progress..</PageLoadingSpinner>
        ) : null}

        {isFetched && data && isResultReady(data?.state) ? (
          <div
            id="step-analysis-creation-done"
            className="flex w-full gap-2 max-h-[60rem]"
          >
            <div className="w-1/2 overflow-y-auto">
              {/* START: ANALYSIS UTILITY BUTTONS */}
              <div className="flex gap-4 mt-6 mb-12">
                <Button
                  id="step-pitch-rate-btn"
                  variant={ButtonVariant.Secondary}
                  onClick={() => routeToPitchCalculator(batchId)}
                >
                  Pitch Calculator
                </Button>
                <Button
                  id="step-download-pdf"
                  variant={ButtonVariant.Secondary}
                  disabled={!isFetched}
                  isLoading={isReportGenerating}
                  onClick={generateReport}
                >
                  {t('PDF Report')}
                </Button>

                <Button
                  id="step-export-csv"
                  variant={ButtonVariant.Secondary}
                  disabled={!isFetched}
                  isLoading={isExporting}
                  onClick={exportReport}
                >
                  {t('CSV Export')}
                </Button>
              </div>
              {/* END: ANALYSIS UTILITY BUTTONS */}

              {/* START: CONZ VIAB BUD */}
              <div className="flex flex-col gap-10 mb-20">
                <div id="step-concentration">
                  <ConcentrationPercentage concentration={data.resultStat.conz} />
                </div>
                <div>
                  {isViabMethod && (
                    <ViableConcentrationPercentage
                      concentration={data.resultStat.conz}
                      viability={data.resultStat.viab}
                    />
                  )}
                </div>
                <div id="step-budding">
                  <BuddingIndexPercentage bud={buddingIndex} />
                </div>
                {isViabMethod && (
                  <div id="step-viability">
                    <ViabilityPercentage viability={data.resultStat.viab} />
                  </div>
                )}
              </div>
              {/* END: CONZ VIAB BUD */}

              {/* START: WARNING */}
              {hasStandardDeviationWarning ? (
                <div className="mb-20">
                  <Warning
                    message="warnings.standardDeviation"
                    level={WarningLevel.Info}
                  />
                </div>
              ) : null}
              {!withinConcentrationRange ? (
                <div className="mb-20">
                  <Warning message="warnings.concOutOfRange" level={WarningLevel.Info} />
                </div>
              ) : null}
              {/* <Warning message="warnings.concOutOfRange" level={WarningLevel.Info} /> */}
              {/* END: WARNING */}

              <HistogramChart histogram={histogram} />
            </div>

            <div className="flex flex-col w-1/2 overflow-y-auto">
              <div id="step-analysis-general-info">
                <AnalysisInfo batch={data} />
              </div>
              <div id="step-individual-image">
                <PictureCardContainer batch={data} />
              </div>
            </div>
          </div>
        ) : null}
      </MeasurementOverviewLayout.Body>
    </MeasurementOverviewLayout>
  );
};
